import { create } from 'zustand';
import {
  createJSONStorage,
  devtools,
  type NamedSet,
  persist,
  type StateStorage,
} from 'zustand/middleware';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import type { Subsidiary } from '@revelio/core';

import { kibanaLogger } from '../../shared/logging/kibana-logger';
import type {
  DataBuilderBooleanFilterInput,
  DataBuilderCompanyMapper,
  DataBuilderCompanySets,
  DataBuilderDateFilterInput,
  DataBuilderFormat,
  DataBuilderModelVersions,
  DataBuilderNumericFilterInput,
  DataBuilderSelectionFilter,
  DataBuilderStringFilterInput,
} from './api/data-builder-api.types';
import {
  classifyKey,
  toFetchKey,
} from './components/custom-filters/data-builder-selection-list-mapping';
import type { CustomFilterDefinition } from './datasets/deliverable-column-config';

export type DeliveryFormat = DataBuilderFormat;
export type DeliveryMethod = 'download' | 'snowflake' | 's3';
export type SnowflakeDeliveryConfig = {
  database: string;
  schema: string;
  table: string;
};
export type S3DeliveryConfig = {
  bucket: string;
  path: string;
  format: DeliveryFormat;
};

export type ModelVersionSelections = Partial<
  Record<keyof DataBuilderModelVersions, number | string>
>;

export type DataBuilderCompanySelections = Record<
  string,
  Subsidiary & { primary_name?: string; label?: string }
>;
export type DataBuilderCompanySet =
  DataBuilderCompanySets['company_sets'][number];
export type CompanySelectionGateStatus = 'idle' | 'pending' | 'error';

export type DatasetInstance = {
  instanceId: string;
  datasetKey: string;
  customLabel?: string;
};

export type StringFilterOperator = DataBuilderStringFilterInput['operator'];
export type SelectionListFilterOperator =
  DataBuilderSelectionFilter['operator'];
export type NumericFilterOperator = DataBuilderNumericFilterInput['operator'];
export type DateFilterOperator = DataBuilderDateFilterInput['operator'];
export type BooleanFilterOperator = DataBuilderBooleanFilterInput['operator'];
export type FilterOperator =
  | StringFilterOperator
  | SelectionListFilterOperator
  | NumericFilterOperator
  | DateFilterOperator
  | BooleanFilterOperator;

export type SelectionListSelectedItem = {
  id: string;
  label: string;
  selectionListId: string;
};

export type CustomFilterConditionType =
  | 'string'
  | 'selection_list'
  | 'numeric'
  | 'date'
  | 'boolean';

export type CustomFilterCondition = {
  type?: CustomFilterConditionType;
  field: string;
  groupKey?: string | null;
  operator: FilterOperator;
  value: string[];
  modifier?: string | null;
  selectionListItems?: SelectionListSelectedItem[];
};

export type CustomFilter = {
  id: string;
  name: string;
  conditions: CustomFilterCondition[];
  binary: 'AND' | 'OR' | null;
};

export type DataBuilderCustomColumnStep = {
  case: CustomFilter | null;
  then: string;
};

export type DataBuilderCustomColumn = {
  id: string;
  name: string;
  steps: DataBuilderCustomColumnStep[];
};

export type DataBuilderSettingValue = string | boolean;
export type PrunableSelectionListField = 'role' | 'skill';
export type AvailableCustomFilterDefinitions = {
  general: CustomFilterDefinition[];
  byBreakdown: Record<string, CustomFilterDefinition[]>;
};

export type PruneEvent = {
  field: PrunableSelectionListField;
  removedFiltersCount: number;
  uoaSelectionsCleared: boolean;
  timestamp: number;
};

export type DatasetInstanceConfig = {
  settings: Record<string, DataBuilderSettingValue>;
  unitOfAnalysis: string | null;
  unitOfAnalysisSelections: SelectionListSelectedItem[];
  uoaSetAt: number | null;
  columnKeysByBreakdown: Record<string, string[]>;
  companySelections: DataBuilderCompanySelections;
  companySets: DataBuilderCompanySet[] | null;
  companyMapper: DataBuilderCompanyMapper | null;
  customFilters: CustomFilter[];
  customFiltersByBreakdown: Record<string, CustomFilter[]>;
  customColumnsByBreakdown: Record<string, DataBuilderCustomColumn[]>;
  includedBreakdownIds: string[];
};

const STORAGE_KEY = 'data-builder-v3';

type DataBuilderState = {
  datasetInstances: DatasetInstance[];
  activeInstanceId: string | null;
  loadedSavedDatasetId: string | null;
  configByInstance: Record<string, DatasetInstanceConfig>;
  lagData: number;
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DeliveryFormat;
  estimateDefaultFormat: DeliveryFormat | null;
  downloadFormatUserSet: boolean;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  modelVersions: ModelVersionSelections;
  lastPruneEvent: PruneEvent | null;
  deliverableName: string;
  deliverableClient: string;
  companyMapperRequests: Record<
    string,
    { status: 'pending' | 'error'; token: object }
  >;
  persistError: DataBuilderPersistError | null;
};

export type DataBuilderPersistError = {
  message: string;
  sizeBytes: number;
  key: string;
};

type DataBuilderActions = {
  toggleDatasetType: (
    datasetKey: string,
    initialConfig?: Partial<DatasetInstanceConfig>
  ) => void;
  addDatasetInstance: (
    datasetKey: string,
    initialConfig?: Partial<DatasetInstanceConfig>
  ) => string;
  removeDatasetInstance: (instanceId: string) => void;
  renameDatasetInstance: (instanceId: string, label: string) => void;
  setActiveInstanceId: (instanceId: string | null) => void;

  setSetting: (
    instanceId: string,
    key: string,
    value: DataBuilderSettingValue
  ) => void;
  setUnitOfAnalysis: (
    instanceId: string,
    value: string | null,
    options?: { uoaSetAt?: number | null }
  ) => void;
  setUnitOfAnalysisSelections: (
    instanceId: string,
    items: SelectionListSelectedItem[]
  ) => void;
  setCompanySelections: (
    instanceId: string,
    value: DataBuilderCompanySelections
  ) => void;
  setCompanySets: (instanceId: string, value: DataBuilderCompanySet[]) => void;
  setCompanyMapper: (
    instanceId: string,
    value: DataBuilderCompanyMapper | null
  ) => void;
  startCompanyMapperRequest: (instanceId: string) => {
    complete: () => void;
    fail: () => void;
  };
  resetCompanyMapperRequest: (instanceId: string) => void;
  setColumnKeysForBreakdown: (
    instanceId: string,
    breakdownId: string,
    value: string[]
  ) => void;
  setColumnKey: (
    instanceId: string,
    breakdownId: string,
    key: string,
    selected: boolean
  ) => void;
  addCustomFilter: (instanceId: string, filter: CustomFilter) => void;
  updateCustomFilter: (
    instanceId: string,
    filterIndex: number,
    filter: CustomFilter
  ) => void;
  removeCustomFilter: (instanceId: string, filterIndex: number) => void;
  updateBreakdownCustomFilter: (
    instanceId: string,
    breakdownId: string,
    filterIndex: number,
    filter: CustomFilter
  ) => void;
  addCustomColumn: (
    instanceId: string,
    breakdownId: string,
    column: DataBuilderCustomColumn
  ) => void;
  updateCustomColumn: (
    instanceId: string,
    breakdownId: string,
    columnIndex: number,
    column: DataBuilderCustomColumn
  ) => void;
  removeCustomColumn: (
    instanceId: string,
    breakdownId: string,
    columnIndex: number
  ) => void;

  excludeBreakdown: (instanceId: string, breakdownId: string) => void;
  includeBreakdown: (
    instanceId: string,
    breakdownId: string,
    defaultColumnKeys?: string[]
  ) => void;
  setLagData: (value: number) => void;
  setDeliveryMethod: (value: DeliveryMethod) => void;
  setDeliveryFormat: (value: DeliveryFormat) => void;
  setEstimateDefaultFormat: (value: DeliveryFormat | null) => void;
  setSnowflakeConfig: (value: SnowflakeDeliveryConfig | null) => void;
  setS3Config: (value: S3DeliveryConfig | null) => void;
  setDeliverableName: (value: string) => void;
  setDeliverableClient: (value: string) => void;
  setModelVersion: (
    key: keyof DataBuilderModelVersions,
    value: number | string | undefined
  ) => void;
  pruneInvalidCustomFilters: (
    instanceId: string,
    availableFilters: AvailableCustomFilterDefinitions
  ) => number;
  pruneSelectionListConditions: (
    selectionListField: PrunableSelectionListField
  ) => { removedFiltersCount: number; uoaSelectionsCleared: boolean };
  resetModelVersions: (defaults: ModelVersionSelections) => void;
  resetSelections: () => void;
  setPersistError: (error: DataBuilderPersistError | null) => void;
};

export type DataBuilderStore = DataBuilderState & {
  reset: () => void;
  actions: DataBuilderActions;
};

const createDefaultInstanceConfig = (): DatasetInstanceConfig => ({
  settings: {},
  unitOfAnalysis: null,
  unitOfAnalysisSelections: [],
  uoaSetAt: null,
  columnKeysByBreakdown: {},
  companySelections: {},
  companySets: null,
  companyMapper: null,
  customFilters: [],
  customFiltersByBreakdown: {},
  customColumnsByBreakdown: {},
  includedBreakdownIds: [],
});

const generateInstanceId = (): string => crypto.randomUUID();

const getInitialState = (): DataBuilderState => ({
  datasetInstances: [],
  activeInstanceId: null,
  loadedSavedDatasetId: null,
  configByInstance: {},
  lagData: 0,
  deliveryMethod: 'download',
  deliveryFormat: 'excel',
  estimateDefaultFormat: null,
  downloadFormatUserSet: false,
  snowflakeConfig: null,
  s3Config: null,
  modelVersions: {},
  lastPruneEvent: null,
  deliverableName: '',
  deliverableClient: '',
  companyMapperRequests: {},
  persistError: null,
});

const canUseSessionStorage = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.sessionStorage;
  } catch {
    return false;
  }
};

const sessionStorageStorage: StateStorage = {
  getItem: (name) => {
    if (!canUseSessionStorage()) return null;
    try {
      return window.sessionStorage.getItem(name);
    } catch {
      // Storage is unreadable (Safari private mode, blocked policies, etc.).
      // Falling back to null lets persist hydrate from initial state instead
      // of crashing during store init — before any React subscriber exists.
      return null;
    }
  },
  setItem: (name, value) => {
    if (!canUseSessionStorage()) return;
    const { persistError, actions } = useDataBuilderStore.getState();
    try {
      window.sessionStorage.setItem(name, value);
      if (persistError !== null) {
        actions.setPersistError(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const bytes = new Blob([value]).size;
      console.error('[DataBuilder persist] setItem failed', error);
      kibanaLogger.error(
        `data-builder persist setItem failed: ${message} (size=${bytes}B, key=${name})`,
        { context: 'data-builder-persist' }
      );

      if (persistError === null) {
        actions.setPersistError({
          message,
          sizeBytes: bytes,
          key: name,
        });
      }
    }
  },
  removeItem: (name) => {
    if (!canUseSessionStorage()) return;
    try {
      window.sessionStorage.removeItem(name);
    } catch {
      // No-op: same storage blocked / unavailable scenarios as getItem.
    }
  },
};

const ensureFilterId = (filter: CustomFilter): CustomFilter => {
  if ('id' in filter && typeof filter.id === 'string') {
    return filter;
  }
  return { ...filter, id: crypto.randomUUID() };
};

const ensureCustomColumnId = (
  column: DataBuilderCustomColumn
): DataBuilderCustomColumn => {
  const steps = column.steps.map((step) => {
    if (step.case === null) {
      return step;
    }
    return { ...step, case: ensureFilterId(step.case) };
  });

  if ('id' in column && typeof column.id === 'string') {
    return { ...column, steps };
  }
  return { ...column, id: crypto.randomUUID(), steps };
};

const ensurePersistedConfigIds = (
  config: DatasetInstanceConfig
): DatasetInstanceConfig => ({
  ...config,
  settings: config.settings ?? {},
  unitOfAnalysisSelections: config.unitOfAnalysisSelections ?? [],
  uoaSetAt: typeof config.uoaSetAt === 'number' ? config.uoaSetAt : null,
  companySets: config.companySets ?? null,
  customFilters: (config.customFilters ?? []).map(ensureFilterId),
  customFiltersByBreakdown: Object.fromEntries(
    Object.entries(config.customFiltersByBreakdown ?? {}).map(
      ([breakdownId, filters]) => [breakdownId, filters.map(ensureFilterId)]
    )
  ),
  customColumnsByBreakdown: Object.fromEntries(
    Object.entries(config.customColumnsByBreakdown ?? {}).map(
      ([breakdownId, columns]) => [
        breakdownId,
        columns.map(ensureCustomColumnId),
      ]
    )
  ),
  includedBreakdownIds: Array.isArray(config.includedBreakdownIds)
    ? config.includedBreakdownIds
    : [],
});

const mergePersistedState = (
  persisted: unknown,
  current: DataBuilderStore
): DataBuilderStore => {
  if (!persisted || typeof persisted !== 'object') {
    return current;
  }
  const p = persisted as {
    configByInstance?: Record<string, DatasetInstanceConfig>;
    [key: string]: unknown;
  };
  const deliveryMethod =
    p.deliveryMethod === 'sftp' ? 'download' : p.deliveryMethod;
  const configByInstance = p.configByInstance;
  if (!configByInstance) {
    return { ...current, ...p, deliveryMethod } as DataBuilderStore;
  }
  const normalized = Object.fromEntries(
    Object.entries(configByInstance).map(([id, config]) => [
      id,
      ensurePersistedConfigIds(config),
    ])
  );
  return {
    ...current,
    ...p,
    deliveryMethod,
    configByInstance: normalized,
  } as DataBuilderStore;
};

const updateInstanceConfig = (
  state: DataBuilderStore,
  instanceId: string,
  patch: Partial<DatasetInstanceConfig>
) => ({
  configByInstance: {
    ...state.configByInstance,
    [instanceId]: {
      ...(state.configByInstance[instanceId] ?? createDefaultInstanceConfig()),
      ...patch,
    },
  },
});

const filterContainsSelectionListField = (
  filter: CustomFilter,
  selectionListField: PrunableSelectionListField
): boolean =>
  filter.conditions.some(
    (condition) =>
      condition.type === 'selection_list' &&
      condition.field === selectionListField
  );

type PrunedFilterResult = {
  filter: CustomFilter | null;
  removedConditionsCount: number;
};

const matchesFilterDefinition = (
  condition: CustomFilterCondition,
  filterDefinition: CustomFilterDefinition
) => {
  if (filterDefinition.type === 'selection_list') {
    return (
      condition.field === filterDefinition.selectionList ||
      filterDefinition.keys.includes(condition.field)
    );
  }

  return condition.field === filterDefinition.key;
};

const getMatchingFilterDefinition = (
  condition: CustomFilterCondition,
  availableFilters: CustomFilterDefinition[]
) =>
  availableFilters.find((filterDefinition) =>
    matchesFilterDefinition(condition, filterDefinition)
  );

const isCustomFilterConditionAvailable = (
  condition: CustomFilterCondition,
  availableFilters: CustomFilterDefinition[]
) => {
  const matchingFilterDefinition = getMatchingFilterDefinition(
    condition,
    availableFilters
  );

  if (matchingFilterDefinition === undefined) {
    return false;
  }

  const allowedOperators =
    matchingFilterDefinition.operators as readonly string[];
  if (!allowedOperators.includes(condition.operator)) {
    return false;
  }

  if (matchingFilterDefinition.modifiers.length > 0) {
    if (condition.modifier === null || condition.modifier === undefined) {
      return false;
    }

    const isModifierAvailable = matchingFilterDefinition.modifiers.some(
      (modifier) => modifier.key === condition.modifier
    );

    if (!isModifierAvailable) {
      return false;
    }
  }

  if (matchingFilterDefinition.type === 'selection_list') {
    return (
      condition.selectionListItems !== undefined &&
      condition.selectionListItems.length > 0
    );
  }

  return true;
};

const pruneInvalidConditionsFromFilter = (
  filter: CustomFilter,
  availableFilters: CustomFilterDefinition[]
): PrunedFilterResult => {
  const nextConditions = filter.conditions.filter((condition) =>
    isCustomFilterConditionAvailable(condition, availableFilters)
  );
  const removedConditionsCount =
    filter.conditions.length - nextConditions.length;

  if (nextConditions.length === 0) {
    return {
      filter: null,
      removedConditionsCount,
    };
  }

  return {
    filter:
      removedConditionsCount > 0
        ? {
            ...filter,
            conditions: nextConditions,
            binary: nextConditions.length > 1 ? filter.binary : null,
          }
        : filter,
    removedConditionsCount,
  };
};

const pruneSelectionListConditionsFromFilters = (
  filters: CustomFilter[],
  selectionListField: PrunableSelectionListField
) => {
  const nextFilters = filters.filter(
    (filter) => !filterContainsSelectionListField(filter, selectionListField)
  );

  return {
    filters: nextFilters,
    removedFiltersCount: filters.length - nextFilters.length,
  };
};

const pruneInvalidConditionsFromFilters = (
  filters: CustomFilter[],
  availableFilters: CustomFilterDefinition[]
) => {
  const initialResult = {
    filters: [] as CustomFilter[],
    removedConditionsCount: 0,
  };

  return filters.reduce((result, filter) => {
    const prunedFilter = pruneInvalidConditionsFromFilter(
      filter,
      availableFilters
    );

    return {
      filters:
        prunedFilter.filter === null
          ? result.filters
          : [...result.filters, prunedFilter.filter],
      removedConditionsCount:
        result.removedConditionsCount + prunedFilter.removedConditionsCount,
    };
  }, initialResult);
};

const pruneSelectionListConditionsFromCustomColumns = (
  customColumns: DataBuilderCustomColumn[],
  selectionListField: PrunableSelectionListField
) => {
  const nextCustomColumns = customColumns.filter(
    (customColumn) =>
      !customColumn.steps.some(
        (step) =>
          step.case !== null &&
          filterContainsSelectionListField(step.case, selectionListField)
      )
  );

  return {
    customColumns: nextCustomColumns,
    removedFiltersCount: customColumns.length - nextCustomColumns.length,
  };
};

const TAXONOMY_KEY_TO_PRUNE_FIELD: Partial<
  Record<keyof DataBuilderModelVersions, PrunableSelectionListField>
> = {
  job_taxonomy_version: 'role',
  job_taxonomy_id: 'role',
  job_taxonomy_patch_version: 'role',
  skill_taxonomy_version: 'skill',
  skill_taxonomy_id: 'skill',
  skill_taxonomy_patch_version: 'skill',
};

const ROLE_UOA_VALUES = new Set(['occupation', 'role']);
const SKILL_UOA_VALUES = new Set(['skill']);

const matchesTaxonomySensitiveUnitOfAnalysis = (
  unitOfAnalysis: string | null,
  selectionListField: PrunableSelectionListField
): boolean => {
  if (unitOfAnalysis === null) {
    return false;
  }

  const normalizedUnit = unitOfAnalysis.toLowerCase();
  if (selectionListField === 'role') {
    return ROLE_UOA_VALUES.has(normalizedUnit);
  }

  return SKILL_UOA_VALUES.has(normalizedUnit);
};

const createActions = (
  set: NamedSet<DataBuilderStore>,
  get: () => DataBuilderStore
): DataBuilderActions => ({
  toggleDatasetType: (datasetKey, initialConfig) =>
    set(
      (state) => {
        const hasType = state.datasetInstances.some(
          (d) => d.datasetKey === datasetKey
        );
        if (hasType) {
          const removedIds = new Set(
            state.datasetInstances
              .filter((d) => d.datasetKey === datasetKey)
              .map((d) => d.instanceId)
          );
          const nextInstances = state.datasetInstances.filter(
            (d) => d.datasetKey !== datasetKey
          );
          const nextConfig = { ...state.configByInstance };
          for (const id of removedIds) {
            delete nextConfig[id];
          }
          const needNewActive =
            state.activeInstanceId !== null &&
            removedIds.has(state.activeInstanceId);
          return {
            datasetInstances: nextInstances,
            configByInstance: nextConfig,
            activeInstanceId: needNewActive
              ? (nextInstances[0]?.instanceId ?? null)
              : state.activeInstanceId,
          };
        }
        const instanceId = generateInstanceId();
        return {
          datasetInstances: [
            ...state.datasetInstances,
            { instanceId, datasetKey },
          ],
          configByInstance: {
            ...state.configByInstance,
            [instanceId]: {
              ...createDefaultInstanceConfig(),
              ...initialConfig,
            },
          },
        };
      },
      false,
      'toggleDatasetType'
    ),

  addDatasetInstance: (datasetKey, initialConfig) => {
    const instanceId = generateInstanceId();
    set(
      (state) => ({
        datasetInstances: [
          ...state.datasetInstances,
          { instanceId, datasetKey },
        ],
        configByInstance: {
          ...state.configByInstance,
          [instanceId]: { ...createDefaultInstanceConfig(), ...initialConfig },
        },
      }),
      false,
      'addDatasetInstance'
    );
    return instanceId;
  },

  removeDatasetInstance: (instanceId) =>
    set(
      (state) => {
        const nextInstances = state.datasetInstances.filter(
          (d) => d.instanceId !== instanceId
        );
        const { [instanceId]: _removed, ...nextConfig } =
          state.configByInstance;
        const { [instanceId]: _removedRequest, ...nextMapperRequests } =
          state.companyMapperRequests;
        const needNewActive = state.activeInstanceId === instanceId;
        return {
          datasetInstances: nextInstances,
          configByInstance: nextConfig,
          companyMapperRequests: nextMapperRequests,
          activeInstanceId: needNewActive
            ? (nextInstances[0]?.instanceId ?? null)
            : state.activeInstanceId,
        };
      },
      false,
      'removeDatasetInstance'
    ),

  renameDatasetInstance: (instanceId, label) =>
    set(
      (state) => ({
        datasetInstances: state.datasetInstances.map((d) =>
          d.instanceId === instanceId
            ? { ...d, customLabel: label.trim() || undefined }
            : d
        ),
      }),
      false,
      'renameDatasetInstance'
    ),

  setActiveInstanceId: (instanceId) =>
    set({ activeInstanceId: instanceId }, false, 'setActiveInstanceId'),

  setSetting: (instanceId, key, value) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          settings: {
            ...config.settings,
            [key]: value,
          },
        });
      },
      false,
      'setSetting'
    ),

  setUnitOfAnalysis: (instanceId, value, options) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const isChangingType = config.unitOfAnalysis !== value;
        const isLeavingCompany =
          isChangingType && config.unitOfAnalysis === 'company';
        const nextUoaSetAt = (() => {
          if (options?.uoaSetAt !== undefined) {
            return options.uoaSetAt;
          }
          if (!isChangingType) {
            return config.uoaSetAt;
          }
          if (value === null) {
            return null;
          }
          return Date.now();
        })();
        const nextInstanceConfig = updateInstanceConfig(state, instanceId, {
          unitOfAnalysis: value,
          uoaSetAt: nextUoaSetAt,
          ...(isChangingType ? { unitOfAnalysisSelections: [] } : {}),
          ...(isLeavingCompany
            ? { companySelections: {}, companySets: null, companyMapper: null }
            : {}),
        });
        if (!isChangingType && value !== null) {
          return nextInstanceConfig;
        }
        const { [instanceId]: _removedRequest, ...nextMapperRequests } =
          state.companyMapperRequests;
        return {
          ...nextInstanceConfig,
          companyMapperRequests: nextMapperRequests,
        };
      },
      false,
      'setUnitOfAnalysis'
    ),

  setUnitOfAnalysisSelections: (instanceId, items) =>
    set(
      (state) =>
        updateInstanceConfig(state, instanceId, {
          unitOfAnalysisSelections: items,
        }),
      false,
      'setUnitOfAnalysisSelections'
    ),

  setCompanySelections: (instanceId, value) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          companySelections: value,
          companySets: null,
          ...(config.unitOfAnalysis === 'company'
            ? { uoaSetAt: Date.now() }
            : {}),
        });
      },
      false,
      'setCompanySelections'
    ),

  setCompanySets: (instanceId, value) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          companySets: value,
          companySelections: {},
          companyMapper: null,
          ...(config.unitOfAnalysis === 'company'
            ? { uoaSetAt: Date.now() }
            : {}),
        });
      },
      false,
      'setCompanySets'
    ),

  setCompanyMapper: (instanceId, value) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          companyMapper: value,
          companySets: null,
          ...(config.unitOfAnalysis === 'company'
            ? { uoaSetAt: Date.now() }
            : {}),
        });
      },
      false,
      'setCompanyMapper'
    ),

  startCompanyMapperRequest: (instanceId) => {
    const token = {};
    set(
      (state) => ({
        companyMapperRequests: {
          ...state.companyMapperRequests,
          [instanceId]: { status: 'pending', token },
        },
      }),
      false,
      'startCompanyMapperRequest'
    );
    return {
      complete: () => {
        set(
          (state) => {
            if (state.companyMapperRequests[instanceId]?.token !== token) {
              return state;
            }
            const { [instanceId]: _removedRequest, ...nextMapperRequests } =
              state.companyMapperRequests;
            return { companyMapperRequests: nextMapperRequests };
          },
          false,
          'startCompanyMapperRequest/complete'
        );
      },
      fail: () => {
        set(
          (state) => {
            if (state.companyMapperRequests[instanceId]?.token !== token) {
              return state;
            }
            return {
              companyMapperRequests: {
                ...state.companyMapperRequests,
                [instanceId]: { status: 'error', token },
              },
            };
          },
          false,
          'startCompanyMapperRequest/fail'
        );
      },
    };
  },

  resetCompanyMapperRequest: (instanceId) =>
    set(
      (state) => {
        if (!(instanceId in state.companyMapperRequests)) {
          return state;
        }
        const { [instanceId]: _removedRequest, ...nextMapperRequests } =
          state.companyMapperRequests;
        return { companyMapperRequests: nextMapperRequests };
      },
      false,
      'resetCompanyMapperRequest'
    ),

  setColumnKeysForBreakdown: (instanceId, breakdownId, value) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          columnKeysByBreakdown: {
            ...config.columnKeysByBreakdown,
            [breakdownId]: value,
          },
        });
      },
      false,
      'setColumnKeysForBreakdown'
    ),

  setColumnKey: (instanceId, breakdownId, key, selected) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const current = config.columnKeysByBreakdown[breakdownId] ?? [];
        const has = current.includes(key);
        if (has === selected) {
          return state;
        }
        const next = selected
          ? [...current, key]
          : current.filter((k) => k !== key);
        return updateInstanceConfig(state, instanceId, {
          columnKeysByBreakdown: {
            ...config.columnKeysByBreakdown,
            [breakdownId]: next,
          },
        });
      },
      false,
      'setColumnKey'
    ),

  addCustomFilter: (instanceId, filter) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          customFilters: [...(config.customFilters ?? []), filter],
        });
      },
      false,
      'addCustomFilter'
    ),

  updateCustomFilter: (instanceId, filterIndex, filter) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const next = [...(config.customFilters ?? [])];
        next[filterIndex] = filter;
        return updateInstanceConfig(state, instanceId, {
          customFilters: next,
        });
      },
      false,
      'updateCustomFilter'
    ),

  removeCustomFilter: (instanceId, filterIndex) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        return updateInstanceConfig(state, instanceId, {
          customFilters: (config.customFilters ?? []).filter(
            (_, i) => i !== filterIndex
          ),
        });
      },
      false,
      'removeCustomFilter'
    ),

  updateBreakdownCustomFilter: (instanceId, breakdownId, filterIndex, filter) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const currentFilters =
          config.customFiltersByBreakdown[breakdownId] ?? [];
        const nextFilters = [...currentFilters];
        nextFilters[filterIndex] = filter;
        return updateInstanceConfig(state, instanceId, {
          customFiltersByBreakdown: {
            ...config.customFiltersByBreakdown,
            [breakdownId]: nextFilters,
          },
        });
      },
      false,
      'updateBreakdownCustomFilter'
    ),

  addCustomColumn: (instanceId, breakdownId, column) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const currentColumns =
          config.customColumnsByBreakdown[breakdownId] ?? [];
        return updateInstanceConfig(state, instanceId, {
          customColumnsByBreakdown: {
            ...config.customColumnsByBreakdown,
            [breakdownId]: [...currentColumns, column],
          },
        });
      },
      false,
      'addCustomColumn'
    ),

  updateCustomColumn: (instanceId, breakdownId, columnIndex, column) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const currentColumns =
          config.customColumnsByBreakdown[breakdownId] ?? [];
        const nextColumns = [...currentColumns];
        nextColumns[columnIndex] = column;
        return updateInstanceConfig(state, instanceId, {
          customColumnsByBreakdown: {
            ...config.customColumnsByBreakdown,
            [breakdownId]: nextColumns,
          },
        });
      },
      false,
      'updateCustomColumn'
    ),

  removeCustomColumn: (instanceId, breakdownId, columnIndex) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        const currentColumns =
          config.customColumnsByBreakdown[breakdownId] ?? [];
        return updateInstanceConfig(state, instanceId, {
          customColumnsByBreakdown: {
            ...config.customColumnsByBreakdown,
            [breakdownId]: currentColumns.filter((_, i) => i !== columnIndex),
          },
        });
      },
      false,
      'removeCustomColumn'
    ),

  excludeBreakdown: (instanceId, breakdownId) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        if (!config.includedBreakdownIds.includes(breakdownId)) {
          return {};
        }
        const { [breakdownId]: _removedColumns, ...nextColumnKeysByBreakdown } =
          config.columnKeysByBreakdown;
        const {
          [breakdownId]: _removedFilters,
          ...nextCustomFiltersByBreakdown
        } = config.customFiltersByBreakdown;
        const {
          [breakdownId]: _removedCustomColumns,
          ...nextCustomColumnsByBreakdown
        } = config.customColumnsByBreakdown;
        return updateInstanceConfig(state, instanceId, {
          includedBreakdownIds: config.includedBreakdownIds.filter(
            (id) => id !== breakdownId
          ),
          columnKeysByBreakdown: nextColumnKeysByBreakdown,
          customFiltersByBreakdown: nextCustomFiltersByBreakdown,
          customColumnsByBreakdown: nextCustomColumnsByBreakdown,
        });
      },
      false,
      'excludeBreakdown'
    ),

  includeBreakdown: (instanceId, breakdownId, defaultColumnKeys) =>
    set(
      (state) => {
        const config =
          state.configByInstance[instanceId] ?? createDefaultInstanceConfig();
        if (config.includedBreakdownIds.includes(breakdownId)) {
          return {};
        }
        const nextColumnKeysByBreakdown =
          defaultColumnKeys !== undefined && defaultColumnKeys.length > 0
            ? {
                ...config.columnKeysByBreakdown,
                [breakdownId]: defaultColumnKeys,
              }
            : config.columnKeysByBreakdown;
        return updateInstanceConfig(state, instanceId, {
          includedBreakdownIds: [...config.includedBreakdownIds, breakdownId],
          columnKeysByBreakdown: nextColumnKeysByBreakdown,
        });
      },
      false,
      'includeBreakdown'
    ),

  setDeliverableName: (value) =>
    set({ deliverableName: value }, false, 'setDeliverableName'),

  setDeliverableClient: (value) =>
    set({ deliverableClient: value }, false, 'setDeliverableClient'),

  setLagData: (value) => set({ lagData: value }, false, 'setLagData'),

  setDeliveryMethod: (value) =>
    set({ deliveryMethod: value }, false, 'setDeliveryMethod'),

  setDeliveryFormat: (value) =>
    set(
      { deliveryFormat: value, downloadFormatUserSet: true },
      false,
      'setDeliveryFormat'
    ),

  setEstimateDefaultFormat: (value) =>
    set(
      (state) => ({
        estimateDefaultFormat: value,
        ...(state.downloadFormatUserSet || value === null
          ? {}
          : { deliveryFormat: value }),
      }),
      false,
      'setEstimateDefaultFormat'
    ),

  setSnowflakeConfig: (value) =>
    set({ snowflakeConfig: value }, false, 'setSnowflakeConfig'),

  setS3Config: (value) => set({ s3Config: value }, false, 'setS3Config'),

  setModelVersion: (key, value) => {
    const previousValue = get().modelVersions[key];
    const pruneField = TAXONOMY_KEY_TO_PRUNE_FIELD[key];

    set(
      (state) => ({
        modelVersions:
          value === undefined
            ? Object.fromEntries(
                Object.entries(state.modelVersions).filter(
                  ([modelKey]) => modelKey !== key
                )
              )
            : {
                ...state.modelVersions,
                [key]: value,
              },
      }),
      false,
      'setModelVersion'
    );

    if (previousValue === value) {
      return;
    }

    if (pruneField === undefined) {
      return;
    }

    const result = get().actions.pruneSelectionListConditions(pruneField);

    if (result.removedFiltersCount > 0 || result.uoaSelectionsCleared) {
      set(
        {
          lastPruneEvent: {
            field: pruneField,
            ...result,
            timestamp: Date.now(),
          },
        },
        false,
        'setModelVersion/pruneEvent'
      );
    }
  },

  pruneInvalidCustomFilters: (instanceId, availableFilters) => {
    let removedConditionsCount = 0;

    set(
      (state) => {
        const config = state.configByInstance[instanceId];

        if (config === undefined) {
          return state;
        }

        const prunedCustomFilters = pruneInvalidConditionsFromFilters(
          config.customFilters,
          availableFilters.general
        );
        const prunedCustomFiltersByBreakdown = Object.fromEntries(
          Object.entries(config.customFiltersByBreakdown).map(
            ([breakdownId, filters]) => {
              const prunedFilters = pruneInvalidConditionsFromFilters(
                filters,
                availableFilters.byBreakdown[breakdownId] ?? []
              );

              removedConditionsCount += prunedFilters.removedConditionsCount;

              return [breakdownId, prunedFilters.filters];
            }
          )
        );

        removedConditionsCount += prunedCustomFilters.removedConditionsCount;

        if (removedConditionsCount === 0) {
          return state;
        }

        return updateInstanceConfig(state, instanceId, {
          customFilters: prunedCustomFilters.filters,
          customFiltersByBreakdown: prunedCustomFiltersByBreakdown,
        });
      },
      false,
      'pruneInvalidCustomFilters'
    );

    return removedConditionsCount;
  },

  pruneSelectionListConditions: (selectionListField) => {
    let removedFiltersCount = 0;
    let uoaSelectionsCleared = false;

    set(
      (state) => {
        const nextConfigByInstance = Object.fromEntries(
          Object.entries(state.configByInstance).map(([instanceId, config]) => {
            const prunedCustomFilters = pruneSelectionListConditionsFromFilters(
              config.customFilters,
              selectionListField
            );
            const prunedCustomFiltersByBreakdown = Object.fromEntries(
              Object.entries(config.customFiltersByBreakdown).map(
                ([breakdownId, filters]) => {
                  const prunedFilters = pruneSelectionListConditionsFromFilters(
                    filters,
                    selectionListField
                  );

                  removedFiltersCount += prunedFilters.removedFiltersCount;

                  return [breakdownId, prunedFilters.filters];
                }
              )
            );
            const prunedCustomColumnsByBreakdown = Object.fromEntries(
              Object.entries(config.customColumnsByBreakdown).map(
                ([breakdownId, customColumns]) => {
                  const prunedCustomColumns =
                    pruneSelectionListConditionsFromCustomColumns(
                      customColumns,
                      selectionListField
                    );

                  removedFiltersCount +=
                    prunedCustomColumns.removedFiltersCount;

                  return [breakdownId, prunedCustomColumns.customColumns];
                }
              )
            );

            removedFiltersCount += prunedCustomFilters.removedFiltersCount;
            const hasAffectedUoaSelections =
              config.unitOfAnalysisSelections.length > 0 &&
              config.unitOfAnalysisSelections.some(
                (item) =>
                  classifyKey(toFetchKey(item.selectionListId)) ===
                  selectionListField
              );
            const hasTaxonomySensitiveUnitOfAnalysis =
              matchesTaxonomySensitiveUnitOfAnalysis(
                config.unitOfAnalysis,
                selectionListField
              );
            const shouldClearUnitOfAnalysis =
              hasAffectedUoaSelections || hasTaxonomySensitiveUnitOfAnalysis;

            if (shouldClearUnitOfAnalysis) {
              uoaSelectionsCleared = true;
            }

            return [
              instanceId,
              {
                ...config,
                customFilters: prunedCustomFilters.filters,
                customFiltersByBreakdown: prunedCustomFiltersByBreakdown,
                customColumnsByBreakdown: prunedCustomColumnsByBreakdown,
                ...(shouldClearUnitOfAnalysis
                  ? { unitOfAnalysis: null, unitOfAnalysisSelections: [] }
                  : {}),
              },
            ];
          })
        );

        if (removedFiltersCount === 0 && !uoaSelectionsCleared) {
          return state;
        }

        return {
          configByInstance: nextConfigByInstance,
        };
      },
      false,
      'pruneSelectionListConditions'
    );

    return { removedFiltersCount, uoaSelectionsCleared };
  },

  resetModelVersions: (defaults) => {
    const previous = get().modelVersions;
    set({ modelVersions: defaults }, false, 'resetModelVersions');

    const changedFields = new Set<PrunableSelectionListField>();
    for (const [key, pruneField] of Object.entries(
      TAXONOMY_KEY_TO_PRUNE_FIELD
    )) {
      const typedKey = key as keyof DataBuilderModelVersions;
      if (previous[typedKey] !== defaults[typedKey]) {
        changedFields.add(pruneField);
      }
    }

    for (const pruneField of changedFields) {
      const result = get().actions.pruneSelectionListConditions(pruneField);
      if (result.removedFiltersCount > 0 || result.uoaSelectionsCleared) {
        set(
          {
            lastPruneEvent: {
              field: pruneField,
              ...result,
              timestamp: Date.now(),
            },
          },
          false,
          'resetModelVersions/pruneEvent'
        );
      }
    }
  },

  resetSelections: () =>
    set(
      (state) => ({
        ...getInitialState(),
        actions: state.actions,
      }),
      false,
      'resetSelections'
    ),

  setPersistError: (error) =>
    set({ persistError: error }, false, 'setPersistError'),
});

export const useDataBuilderStore = create<DataBuilderStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...getInitialState(),
        actions: createActions(set, get),
        reset: () => {
          sessionStorageStorage.removeItem(STORAGE_KEY);
          set(
            (state) => ({
              ...getInitialState(),
              actions: state.actions,
            }),
            false,
            'reset'
          );
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => sessionStorageStorage),
        partialize: (state: DataBuilderStore) => ({
          datasetInstances: state.datasetInstances,
          activeInstanceId: state.activeInstanceId,
          loadedSavedDatasetId: state.loadedSavedDatasetId,
          configByInstance: state.configByInstance,
          lagData: state.lagData,
          deliveryMethod: state.deliveryMethod,
          deliveryFormat: state.deliveryFormat,
          estimateDefaultFormat: state.estimateDefaultFormat,
          downloadFormatUserSet: state.downloadFormatUserSet,
          snowflakeConfig: state.snowflakeConfig,
          s3Config: state.s3Config,
          modelVersions: state.modelVersions,
          deliverableName: state.deliverableName,
          deliverableClient: state.deliverableClient,
        }),
        merge: (persisted, current) => mergePersistedState(persisted, current),
      }
    ),
    { name: 'DataBuilderStore' }
  )
);

export const dataBuilderStore = {
  getState: useDataBuilderStore.getState,
  setState: useDataBuilderStore.setState,
  subscribe: useDataBuilderStore.subscribe,
  get actions() {
    return useDataBuilderStore.getState().actions;
  },
};

export const useDataBuilderSelector = <T>(
  selector: (state: DataBuilderStore) => T,
  equalityFn?: (a: T, b: T) => boolean
) => useStoreWithEqualityFn(useDataBuilderStore, selector, equalityFn);

export const useDataBuilderActions = () =>
  useDataBuilderStore((state) => state.actions);

export const selectDatasetInstances = (state: DataBuilderStore) =>
  state.datasetInstances;

export const selectSelectedDatasetKeys = (state: DataBuilderStore) => [
  ...new Set(state.datasetInstances.map((d) => d.datasetKey)),
];

export const selectActiveInstanceId = (state: DataBuilderStore) =>
  state.activeInstanceId;

export const selectLoadedSavedDatasetId = (state: DataBuilderStore) =>
  state.loadedSavedDatasetId;

export const selectActiveDatasetKey = (
  state: DataBuilderStore
): string | null => {
  if (state.activeInstanceId === null) return null;
  return (
    state.datasetInstances.find((d) => d.instanceId === state.activeInstanceId)
      ?.datasetKey ?? null
  );
};

export const selectActiveInstanceConfig = (
  state: DataBuilderStore
): DatasetInstanceConfig | undefined => {
  if (state.activeInstanceId === null) return undefined;
  return state.configByInstance[state.activeInstanceId];
};

export const selectConfigByInstance = (state: DataBuilderStore) =>
  state.configByInstance;
export const selectLagData = (state: DataBuilderStore) => state.lagData;
export const selectDeliveryFormat = (state: DataBuilderStore) =>
  state.deliveryFormat;
export const selectEstimateDefaultFormat = (state: DataBuilderStore) =>
  state.estimateDefaultFormat;
export const selectDownloadFormatUserSet = (state: DataBuilderStore) =>
  state.downloadFormatUserSet;

export const selectDeliveryMethod = (state: DataBuilderStore) =>
  state.deliveryMethod;

export const selectSnowflakeConfig = (state: DataBuilderStore) =>
  state.snowflakeConfig;

export const selectS3Config = (state: DataBuilderStore) => state.s3Config;

export const selectDeliveryConfigured = (state: DataBuilderStore): boolean => {
  if (state.deliveryMethod === 'snowflake') {
    return state.snowflakeConfig !== null;
  }
  if (state.deliveryMethod === 's3') {
    return state.s3Config !== null;
  }
  return true;
};

export const selectModelVersions = (state: DataBuilderStore) =>
  state.modelVersions;

export const selectLastPruneEvent = (state: DataBuilderStore) =>
  state.lastPruneEvent;

export const selectPersistError = (state: DataBuilderStore) =>
  state.persistError;

export const selectDeliverableName = (state: DataBuilderStore) =>
  state.deliverableName;

export const selectDeliverableClient = (state: DataBuilderStore) =>
  state.deliverableClient;

export const selectCompanyMapperGateStatus = (
  state: DataBuilderStore
): CompanySelectionGateStatus => {
  const statuses = Object.values(state.companyMapperRequests).map(
    (entry) => entry.status
  );
  if (statuses.includes('pending')) {
    return 'pending';
  }
  if (statuses.includes('error')) {
    return 'error';
  }
  return 'idle';
};
